import React, { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Typography, useDimensions } from '@telavita-core/react-design-kit'
import { addMonths, eachMonthOfInterval, endOfMonth, startOfMonth } from 'date-fns'

import ScheduleContainer from 'containers/ScheduleContainer'

import { usePatientPlanType } from 'hooks/usePatientPlanType'
import { creditsPanelRequest } from 'redux/modules/creditsPanel'

import { UserData } from 'models/Auth'
import { Reservation, ReservationRecurrence } from 'models/Reservation'

import ReservationService from 'services/reservation'
import { getProfessionalAvailability } from 'services/professionalAvailabilityService'
import { getCorporateEligibility } from 'services/corporateEligibilityService'
import { ProfessionalRestrictions } from 'services/reservation/professionalRestrictions'

import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { FROM_NPS } from 'settings/_scheduledFrom'
import * as routes from 'settings/_routesSettings'
import { IProfessionalProfile } from 'models/Professional/IProfessionalProfile'

import { useCreateOrder } from 'views/Homepage/context/useCreateOrder'
import { usePlanGroupAllowsReservation } from 'hooks/usePlanGroupAllowsReservation'

import { PatientCreatingReservationRestrictionsModal } from 'containers/ModalManager/Modals/PatientCreatingReservationRestrictionsModal'
import type { PatientCreatingReservationConfirmPayload } from 'containers/ModalManager/Modals/PatientCreatingReservationRestrictionsModal'
import {
  buildSelectedNonRecurrentReservationPairs,
  toReservationCheckoutExceptions,
} from 'utils/toReservationCheckoutExceptions'
import type { ReservationCheckoutExceptionItem } from 'utils/toReservationCheckoutExceptions'
import { invalidatePatientReservationCardCache } from 'utils/invalidatePatientReservationCardCache'

import * as S from './styles'

interface ICreditsPanel {
  creditsPanel: {
    data: {
      available_credits: number
    }
  }
}

interface AuthData {
  auth: {
    userData: {
      person_profile: {
        id: number
      }[]
    }
  }
}

interface ScheduleContainerWrapperProps {
  professional: IProfessionalProfile
}

const FREQUENCY_OF_ENTERPRISE_WITH_RESERVATION = '7 days, 0:00:00'

export function ScheduleContainerWrapper({
  professional
}: ScheduleContainerWrapperProps) {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { isMobile } = useDimensions()
  const {
    isCorporate,
    isEnterprise,
    isParticular
  } = usePatientPlanType()
  const { onCreateOrder, isCreateOrderLoading } = useCreateOrder()

  const [isReservationFlowLoading, setIsReservationFlowLoading] = useState(false)

  const [loadingAvailabilities, setLoadingAvailabilities] = useState(true)
  const [availabilities, setAvailabilities] = useState([])
  const [schedules, setSchedules] = useState([])
  const [maxSchedules, setMaxSchedules] = useState(null)
  const [reservations, setReservations] = useState<Reservation[] | Reservation>(null)
  const [error, setError] = useState(null)
  const [showRestrictionsReservationModal, setShowRestrictionsReservationModal] = useState(false)
  const [restrictions, setRestrictions] = useState<Date[]>([])
  const [nonRecurrentReservationDates, setNonRecurrentReservationDates] = useState<string[]>([])
  const [pendingReservation, setPendingReservation] = useState<ProfessionalRestrictions | null>(null)
  const [
    fetchedAvailabilitiesMonths,
    setFetchedAvailabilitiesMonths,
  ] = useState([])

  const dispatch = useDispatch()

  const creditsPanel = useSelector((state: ICreditsPanel) => state.creditsPanel.data)
  const availableCredits = creditsPanel.available_credits
  const isAuth = useSelector((state: UserData) => state.auth.isAuth)
  const username = useSelector((state: UserData) => state.auth.userData?.username)
  const userData = useSelector((state: AuthData) => state.auth.userData)
  const patientId: number = userData?.person_profile[0].id
  const frequency = useSelector(
    (state: UserData) => state.auth.planSelected?.plan_group.frequency
  )
  const planCode = useSelector(
    (state: UserData) => state.auth.planSelected?.partner_plan_code
  )
  const planGroupCategory = useSelector(
    (state: UserData) => state.auth.planSelected?.plan_group.category
  )

  const planGroupAllowsReservation = usePlanGroupAllowsReservation()

  const allowSchedulingBecomingReservation =
    Boolean(professional?.autoReservationEnabled) && planGroupAllowsReservation

  const fromMonth = startOfMonth(new Date())
  const toMonth = endOfMonth(addMonths(new Date(), 3))

  const { CORPORATE_UNLIMITED } = PlanGroupCategoryEnum

  function goToPlanCheckoutAfterReservation(
    reservationConfirmationSucceeded: boolean,
    reservationNonRecurrentExceptions: ReservationCheckoutExceptionItem[] | undefined
  ) {
    history.push({
      pathname: routes.PLAN_CHECKOUT,
      state: {
        scheduledFrom: FROM_NPS,
        reservationConfirmationSucceeded,
        ...(reservationNonRecurrentExceptions?.length && {
          reservationNonRecurrentExceptions,
        }),
      },
    })
  }

  async function handleSubmit(
    selectedDays: Array<{ startDate: Date; endDate: Date; isRecurring?: boolean }>
  ) {
    const allSelectedAllowRecurringReservation = selectedDays.every(
      (selectedDay) => selectedDay.isRecurring !== false
    )

    const payload = {
      product_group_code: professional?.productGroupCode,
      plan_code: planCode,
      profile_role: 'PSIC',
      person_profile_product_appointment_id: professional?.productAppointmentId,
      scheduledFrom: FROM_NPS,
      autoReservationEnabled: allowSchedulingBecomingReservation,
      username: null,
      schedules: selectedDays.map(selectedDay => {
        return {
          start_date: selectedDay.startDate.toISOString(),
          end_date: selectedDay.endDate.toISOString(),
          ...(selectedDay.isRecurring !== undefined && {
            is_recurring: selectedDay.isRecurring,
          }),
          guests: [
            {
              person_profile_id: professional?.personProfileId
            },
            {
              person_profile_id: patientId
            }
          ]
        }
      })
    }

    const orderCreated = await onCreateOrder(payload)
    if (!orderCreated) return

    if (!allowSchedulingBecomingReservation || !allSelectedAllowRecurringReservation) {
      return
    }

    setIsReservationFlowLoading(true)
    try {
      for (const selectedDay of selectedDays) {
        const patientRestrictionsResponse = await ReservationService.patientRestrictions({
          username: username,
          planCode: planCode
        })

        if (patientRestrictionsResponse.restrictionStatus === 'BLOCKED') {
          goToPlanCheckoutAfterReservation(false, undefined)
          return
        }

        const professionalRestrictionsResponse = await ReservationService.professionalRestrictions({
          professionalUsername: professional.friendlyUrl,
          patientUsername: username,
          startDate: new Date(selectedDay.startDate),
          endDate: new Date(selectedDay.endDate),
          planCode: planCode
        })

        if (professionalRestrictionsResponse.restrictions && professionalRestrictionsResponse.restrictions.length > 0) {
          setRestrictions(professionalRestrictionsResponse.restrictions)
          setPendingReservation(professionalRestrictionsResponse)
          setShowRestrictionsReservationModal(true)
          return
        }

        await ReservationService.createReservation({
          professionalUuid: professional?.uuid,
          patientUsername: username,
          reservationStartDate: professionalRestrictionsResponse.reservationStartDate,
          reservationEndDate: professionalRestrictionsResponse.reservationEndDate,
          recurrent: true
        })
      }
      invalidatePatientReservationCardCache(queryClient)
      goToPlanCheckoutAfterReservation(true, undefined)
    } catch {
      goToPlanCheckoutAfterReservation(false, undefined)
    } finally {
      setIsReservationFlowLoading(false)
    }
  }

  function handleNonRecurrentReservationDate(date: string | null, slotIndex: number) {
    const newNonRecurrentReservationDates = [...nonRecurrentReservationDates]
    newNonRecurrentReservationDates[slotIndex] = date
    setNonRecurrentReservationDates(newNonRecurrentReservationDates)
  }

  const hasNonRecurrentReservationDates = nonRecurrentReservationDates
    .every(date => Boolean(date)) && nonRecurrentReservationDates.length > 0

  const shouldDisableConfirmRestrictionsModal = restrictions.length > 0 && !hasNonRecurrentReservationDates

  async function handleConfirmRestrictionsReservationModal(
    payload?: PatientCreatingReservationConfirmPayload
  ) {
    if (!pendingReservation) return
    if (payload?.skipCreateReservation) {
      setShowRestrictionsReservationModal(false)
      setRestrictions([])
      setPendingReservation(null)
      setNonRecurrentReservationDates([])
      goToPlanCheckoutAfterReservation(false, undefined)
      return
    }
    if (shouldDisableConfirmRestrictionsModal) return
    if (!professional?.uuid) return

    try {
      const selectedPairs = buildSelectedNonRecurrentReservationPairs(
        nonRecurrentReservationDates,
        restrictions
      )
      const reservationResult = await ReservationService.createReservation({
        professionalUuid: professional.uuid,
        patientUsername: username,
        reservationStartDate: pendingReservation.reservationStartDate,
        reservationEndDate: pendingReservation.reservationEndDate,
        recurrent: true,
        nonRecurrentReservations:
          selectedPairs.length > 0 ? selectedPairs.map((item) => item.interval) : null,
      })
      invalidatePatientReservationCardCache(queryClient)
      setShowRestrictionsReservationModal(false)
      setRestrictions([])
      setPendingReservation(null)
      setNonRecurrentReservationDates([])
      goToPlanCheckoutAfterReservation(
        true,
        toReservationCheckoutExceptions(
          selectedPairs.map((item) => item.restrictionDate),
          reservationResult?.nonRecurrentReservations?.successful
        )
      )
    } catch {
      goToPlanCheckoutAfterReservation(false, undefined)
    }
  }

  function handleCloseRestrictionsReservationModal() {
    setShowRestrictionsReservationModal(false)
    setRestrictions([])
    setPendingReservation(null)
    setNonRecurrentReservationDates([])
    goToPlanCheckoutAfterReservation(false, undefined)
  }

  useEffect(() => {
    ReservationService.getReservations({
      username: username,
      isProfessional: false,
    })
      .then((reservations) => {
        setReservations(reservations)
      })
      .catch((error) => {
        console.error(error)
      })
  }, [username])

  useEffect(() => {
    if (!professional) return

    getAvailabilities()
  }, [professional])

  useEffect(() => {
    if (isCorporate) {
      getCorporateEligibility({ username, planCode }).then((res) => {
        if (CORPORATE_UNLIMITED.includes(planGroupCategory)) return

        setMaxSchedules(res.availableCredits)
      })
    } else if (isEnterprise) {
      dispatch(creditsPanelRequest())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planGroupCategory])

  const getAvailabilities = useCallback(async () => {
    const interval = eachMonthOfInterval({ start: fromMonth, end: toMonth })

    for await (const month of interval) {
      await getProfessionalAvailability({
        slug: professional?.friendlyUrl,
        startDate: startOfMonth(month).toISOString(),
        endDate: endOfMonth(month).toISOString(),
        planCode: planCode,
        productCode: professional?.productCode,
        groupBy: 'date',
        username: username || null,
        patientUsername: username || null,
        profileRole: 'PSIC',
        isParticular: isParticular
      })
        .then(({ availabilities, schedules }) => {
          setFetchedAvailabilitiesMonths((prev) => [...prev, month.getMonth()])
          setLoadingAvailabilities(false)
          setAvailabilities((prev) => [...prev, ...availabilities])
          setSchedules((prev) => [...prev, ...schedules])
        })
        .catch((error) => {
          setLoadingAvailabilities(false)
          setAvailabilities([])
          setError(error)
        })
    }
  }, [fromMonth, toMonth, planCode, username, isParticular])

  function getMaxSchedules() {
    if (isCorporate) {
      return maxSchedules
    }

    if (isEnterprise) {
      return availableCredits
    }

    return null
  }

  const recurrenceReservation = Array.isArray(reservations)
    ? reservations?.find(reservation => reservation.recurrence === ReservationRecurrence.WEEKLY)
    : reservations

  function getFrequency() {
    if (isEnterprise && recurrenceReservation) {
      return FREQUENCY_OF_ENTERPRISE_WITH_RESERVATION
    }

    return frequency
  }

  const onMonthChange = (date) => {
    if (!fetchedAvailabilitiesMonths.includes(date.getMonth()))
      setLoadingAvailabilities(true)
  }

  return (
    <S.Container isMobile={isMobile}>
      {!allowSchedulingBecomingReservation && (
        <S.LineBreaker isMobile={isMobile}>
          <div />
          <Typography
            variant='content2'
            center
            weight='bold'
          >
            ESCOLHA UM OU MAIS DIAS
          </Typography>
          <div />
        </S.LineBreaker>
      )}

      <ScheduleContainer
        loading={loadingAvailabilities}
        loadingSubmit={isCreateOrderLoading || isReservationFlowLoading}
        isAdminArea={false}
        fromMonth={fromMonth}
        isPatientAlreadyHasRequiredInfo={true}
        isUserAuthenticated={isAuth}
        cleanSelectedDaysOnSubmit={false}
        handleOpenPatientInfoModal={null}
        toMonth={toMonth}
        availabilities={availabilities}
        schedules={schedules}
        maxSchedules={getMaxSchedules()}
        frequency={getFrequency()}
        reservationDate={recurrenceReservation && !error && recurrenceReservation?.startDate}
        reservationExceptions={
          recurrenceReservation && recurrenceReservation?.recurrencyExceptions
        }
        error={error}
        onSubmit={handleSubmit}
        enableNewPatients={professional ? professional.enableNewPatients : false}
        onMonthChange={onMonthChange}
        isParticular={isParticular}
        allowSchedulingBecomingReservation={allowSchedulingBecomingReservation}
      />

      {showRestrictionsReservationModal && (
        <PatientCreatingReservationRestrictionsModal
          onClose={handleCloseRestrictionsReservationModal}
          restrictions={restrictions}
          patientUsername={username}
          professionalUsername={professional.friendlyUrl}
          onSelectNonRecurrentReservationDate={handleNonRecurrentReservationDate}
          onConfirm={handleConfirmRestrictionsReservationModal}
          confirmDisabled={shouldDisableConfirmRestrictionsModal}
        />
      )}
    </S.Container>
  )
}
